import type {
  Announcement,
  CreateAnnouncementDto,
  UpdateAnnouncementDto,
} from '../../api/announcement.service';
import type { DemoStoreData } from '../store-types';
import { route, type HandlerEntry } from '../handler-registry';

function newId(data: DemoStoreData): string {
  return `ann-${data.announcements.length + 1}-${Date.now().toString(36).slice(-4)}`;
}

function validateCreate(
  body: CreateAnnouncementDto,
): { status: number; message: string } | null {
  if (!body.title || body.title.length < 1 || body.title.length > 200) {
    return { status: 400, message: 'Titlul este obligatoriu (1–200 caractere).' };
  }
  if (
    !body.description ||
    body.description.length < 1 ||
    body.description.length > 4000
  ) {
    return {
      status: 400,
      message: 'Descrierea este obligatorie (1–4000 caractere).',
    };
  }
  if (body.targetAllClub) {
    if (body.targetGroupIds && body.targetGroupIds.length > 0) {
      return {
        status: 400,
        message: 'Nu poți trimite simultan tot clubul și grupuri specifice.',
      };
    }
  } else {
    if (!body.targetGroupIds || body.targetGroupIds.length === 0) {
      return {
        status: 400,
        message: 'Selectează cel puțin un grup destinatar.',
      };
    }
  }
  return null;
}

function callerTargetedGroupIds(data: DemoStoreData): readonly string[] {
  const me = data.currentMemberId;
  const enrolledGroupIds = data.enrollments
    .filter((e) => e.studentTenantAccountId === me && e.status === 'Active')
    .map((e) => e.groupId);
  const coachGroupIds = data.groups
    .filter((g) => g.defaultCoachTenantAccountIds.includes(me))
    .map((g) => g.groupId);
  return Array.from(new Set([...enrolledGroupIds, ...coachGroupIds]));
}

export const announcementsHandlers: readonly HandlerEntry[] = [
  route('GET', /^\/api\/tenants\/([^/]+)\/announcements$/, (ctx, data) => {
    const includeArchived = ctx.query.get('includeArchived') === 'true';
    const all = [...data.announcements].sort((a, b) =>
      a.createdAt < b.createdAt ? 1 : a.createdAt > b.createdAt ? -1 : 0,
    );
    return includeArchived ? all : all.filter((a) => a.status === 'Active');
  }),
  route(
    'GET',
    /^\/api\/tenants\/([^/]+)\/announcements\/([^/]+)$/,
    (ctx, data) => {
      const id = decodeURIComponent(ctx.match[2]);
      const found = data.announcements.find((a) => a.id === id);
      return found ?? { error: { status: 404, message: 'Not found' } };
    },
  ),
  route('POST', /^\/api\/tenants\/([^/]+)\/announcements$/, (ctx) => {
    const body = ctx.body as CreateAnnouncementDto;
    const err = validateCreate(body);
    if (err) return { error: err };
    return {
      mutate: (data: DemoStoreData) => {
        const now = new Date().toISOString();
        const announcement: Announcement = {
          id: newId(data),
          tenantId: data.tenant.tenantId,
          title: body.title,
          description: body.description,
          eventDate: body.eventDate ?? null,
          targetAllClub: body.targetAllClub,
          targetGroupIds: body.targetAllClub ? [] : [...(body.targetGroupIds ?? [])],
          status: 'Active',
          createdByTenantAccountId: data.currentMemberId,
          createdAt: now,
          updatedAt: null,
        };
        return {
          next: { ...data, announcements: [announcement, ...data.announcements] },
          result: announcement,
        };
      },
    };
  }),
  route(
    'PATCH',
    /^\/api\/tenants\/([^/]+)\/announcements\/([^/]+)$/,
    (ctx) => {
      const id = decodeURIComponent(ctx.match[2]);
      const body = ctx.body as UpdateAnnouncementDto;
      return {
        mutate: (data: DemoStoreData) => {
          const existing = data.announcements.find((a) => a.id === id);
          if (!existing) {
            return { next: data, result: null as unknown as Announcement };
          }
          const targetAllClub = body.targetAllClub ?? existing.targetAllClub;
          const targetGroupIds = targetAllClub
            ? []
            : body.targetGroupIds !== undefined
              ? [...body.targetGroupIds]
              : [...existing.targetGroupIds];
          const eventDate = body.clearEventDate
            ? null
            : body.eventDate !== undefined
              ? body.eventDate
              : existing.eventDate;
          const updated: Announcement = {
            ...existing,
            title: body.title ?? existing.title,
            description: body.description ?? existing.description,
            eventDate,
            targetAllClub,
            targetGroupIds,
            updatedAt: new Date().toISOString(),
          };
          return {
            next: {
              ...data,
              announcements: data.announcements.map((a) =>
                a.id === id ? updated : a,
              ),
            },
            result: updated,
          };
        },
      };
    },
  ),
  route(
    'DELETE',
    /^\/api\/tenants\/([^/]+)\/announcements\/([^/]+)\/permanent$/,
    (ctx) => {
      const id = decodeURIComponent(ctx.match[2]);
      return {
        mutate: (data: DemoStoreData) => ({
          next: {
            ...data,
            announcements: data.announcements.filter((a) => a.id !== id),
          },
          result: undefined as void,
        }),
      };
    },
  ),
  route(
    'DELETE',
    /^\/api\/tenants\/([^/]+)\/announcements\/([^/]+)$/,
    (ctx) => {
      const id = decodeURIComponent(ctx.match[2]);
      return {
        mutate: (data: DemoStoreData) => ({
          next: {
            ...data,
            announcements: data.announcements.map((a) =>
              a.id === id
                ? { ...a, status: 'Archived' as const, updatedAt: new Date().toISOString() }
                : a,
            ),
          },
          result: undefined as void,
        }),
      };
    },
  ),
  route('GET', /^\/api\/tenants\/([^/]+)\/me\/announcements$/, (_ctx, data) => {
    const my = new Set(callerTargetedGroupIds(data));
    return [...data.announcements]
      .filter((a) => a.status === 'Active')
      .filter(
        (a) =>
          a.targetAllClub || a.targetGroupIds.some((g) => my.has(g)),
      )
      .sort((a, b) =>
        a.createdAt < b.createdAt ? 1 : a.createdAt > b.createdAt ? -1 : 0,
      );
  }),
];
